import React, { useState, useEffect, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ProgressSpinner } from 'primereact/progressspinner';
import { MultiSelect } from 'primereact/multiselect';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import { formatDateOnly, formatDateWithTime } from '../../../../../utils/dateUtils';
import { useGetVehicleAcceptanceFormHistoryQuery } from '../../../../../api';

const VehicleAcceptanceHistoryDialog = ({ hideDialog, dialogVisible, vehicleAcceptanceForm }) => {
    const { th, getCurrentLanguageId } = useLanguage();

    const [historyData, setHistoryData] = useState([]);
    // State for visible columns based on API response structure
    const [visibleColumns, setVisibleColumns] = useState(['vehicleAcceptDate', 'vehicleAcceptKm', 'chassisNo', 'customerName', 'description', 'statusName', 'createdDate', 'returnMsg']);
    // Reference for DataTable export
    const dt = useRef(null);

    const shouldFetch = dialogVisible && vehicleAcceptanceForm && vehicleAcceptanceForm.id;

    const {
        data: historyRecords,
        isLoading,
        isError,
        error
    } = useGetVehicleAcceptanceFormHistoryQuery(
        { 
            id: vehicleAcceptanceForm?.id,
            pageSize: 50,
            pageNumber: 0,
            languageId: getCurrentLanguageId()
        }, 
        {
            skip: !shouldFetch,
            refetchOnMountOrArgChange: true
        }
    );

    useEffect(() => {
        if (historyRecords && historyRecords.items) {
            setHistoryData(historyRecords.items);
        } else {
            setHistoryData([]);
        }
    }, [historyRecords]);

    const columnConfigs = [
        {
            field: 'vehicleAcceptDate',
            header: th('warranty.vehicleAcceptanceForm.history.vehicleAcceptDate'),
            body: (rowData) => formatDateOnly(rowData.vehicleAcceptDate),
            sortable: true,
            style: { minWidth: '150px' }
        },
        {
            field: 'vehicleAcceptKm',
            header: th('warranty.vehicleAcceptanceForm.history.vehicleAcceptKm'),
            body: (rowData) => (rowData.vehicleAcceptKm ? rowData.vehicleAcceptKm.toLocaleString('tr-TR') + ' km' : ''),
            sortable: true,
            style: { minWidth: '120px' }
        },
        {
            field: 'chassisNo',
            header: th('warranty.vehicleAcceptanceForm.history.chassisNo'),
            sortable: true,
            style: { minWidth: '150px' }
        },
        {
            field: 'customerName',
            header: th('warranty.vehicleAcceptanceForm.history.customerName'),
            sortable: true,
            style: { minWidth: '200px' }
        },
        {
            field: 'description',
            header: th('warranty.vehicleAcceptanceForm.history.description'),
            sortable: true,
            style: { minWidth: '200px' }
        },
        {
            field: 'statusName',
            header: th('warranty.vehicleAcceptanceForm.history.status'),
            sortable: true,
            style: { minWidth: '120px' }
        },
        {
            field: 'createdDate',
            header: th('warranty.vehicleAcceptanceForm.history.createdDate'),
            body: (rowData) => formatDateWithTime(rowData.createdDate),
            sortable: true,
            style: { minWidth: '180px' }
        },
        {
            field: 'returnMsg',
            header: th('warranty.vehicleAcceptanceForm.history.returnMsg'),
            sortable: true,
            style: { minWidth: '200px' }
        }
    ];

    const columnOptions = columnConfigs.map((config) => ({
        label: config.header,
        value: config.field
    }));

    const exportCSV = () => {
        dt.current.exportCSV();
    };

    const headerTemplate = () => {
        return (
            <div className="flex flex-column sm:flex-row w-full justify-content-end align-items-stretch sm:align-items-center gap-2">
                <div className="w-full sm:w-auto">
                    <MultiSelect
                        value={visibleColumns}
                        options={columnOptions}
                        onChange={(e) => setVisibleColumns(e.value)}
                        className="w-full"
                        placeholder={th('warranty.vehicleAcceptanceForm.history.selectColumns')}
                        maxSelectedLabels={3}
                        selectedItemsLabel={th('warranty.vehicleAcceptanceForm.history.selectedItems')}
                        panelStyle={{ width: '300px' }}
                    />
                </div>
                <Button type="button" icon="pi pi-file" label={th('warranty.vehicleAcceptanceForm.history.csvExport')} onClick={exportCSV} className="p-button-outlined w-full sm:w-auto" disabled={!historyData || historyData.length === 0} />
            </div>
        );
    };

    const renderDialogFooter = () => {
        return (
            <div className="flex justify-content-end gap-2">
                <Button label={th('warranty.vehicleAcceptanceForm.history.close')} icon="pi pi-times" outlined onClick={hideDialog} type="button" />
            </div>
        );
    };

    return (
        <Dialog
            visible={dialogVisible}
            style={{ width: '90%', maxWidth: '1200px' }}
            header={`${th('warranty.vehicleAcceptanceForm.history.title')} - ${vehicleAcceptanceForm?.chassisNo || ''}`}
            modal
            className="p-fluid"
            footer={renderDialogFooter()}
            onHide={hideDialog}
            maximizable
        >
            {isLoading && (
                <div className="flex justify-content-center">
                    <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="4" />
                </div>
            )}

            {isError && <div className="p-error">{th('warranty.vehicleAcceptanceForm.history.loadingError').replace('{0}', error?.message || th('warranty.vehicleAcceptanceForm.history.unknownError'))}</div>}

            {!isLoading && !isError && (!historyData || historyData.length === 0) && <div className="text-center p-4">{th('warranty.vehicleAcceptanceForm.history.noHistoryFound')}</div>}

            {!isLoading && !isError && historyData && historyData.length > 0 && (
                <DataTable
                    ref={dt}
                    value={historyData}
                    paginator
                    rows={10}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    className="p-datatable-gridlines"
                    showGridlines
                    stripedRows
                    emptyMessage={th('warranty.vehicleAcceptanceForm.history.emptyMessage')}
                    dataKey="id"
                    tableStyle={{ minWidth: '50rem' }}
                    header={headerTemplate()}
                >
                    {columnConfigs
                        .filter((config) => visibleColumns.includes(config.field))
                        .map((config) => (
                            <Column key={config.field} field={config.field} header={config.header} body={config.body} sortable={config.sortable} style={config.style} />
                        ))}
                </DataTable>
            )}
        </Dialog>
    );
};

export default VehicleAcceptanceHistoryDialog;
